import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { http } from "../lib/api";
import { markSessionReplaced } from "../lib/session";
import { useAuth } from "./AuthContext";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [online, setOnline] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  const listenersRef = useRef(new Set());
  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    let closed = false;

    async function connect() {
      try {
        const { data } = await http.get("/auth/ws-token");
        const backend = process.env.REACT_APP_BACKEND_URL;
        const wsUrl = backend.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(data.token)}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => { retryRef.current = 0; };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            setLastEvent(msg);
            if (msg.event === "session_replaced") { closed = true; markSessionReplaced(); return; }
            if (msg.event === "session_revoked") { closed = true; if (!window.location.pathname.startsWith("/login")) window.location.assign("/login"); return; }
            if (msg.event === "presence_changed") {
              setOnline(msg.payload?.online || []);
            }
            listenersRef.current.forEach((fn) => {
              try { fn(msg); } catch {}
            });
          } catch {}
        };
        ws.onclose = () => {
          if (closed) return;
          retryRef.current += 1;
          const delay = Math.min(5000, 500 * retryRef.current);
          setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        retryRef.current += 1;
        setTimeout(connect, Math.min(5000, 500 * retryRef.current));
      }
    }

    connect();
    return () => {
      closed = true;
      try { wsRef.current?.close(); } catch {}
    };
  }, [user]);

  return (
    <RealtimeContext.Provider value={{ online, lastEvent, subscribe }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
